/**
 * Help search box (E13-F01) — a controlled filter over the table of contents.
 *
 * Scope note: this narrows the TOC, it does NOT hide content. The guide stays a
 * single scrollable page (the approved structure), so filtering the article body
 * would break the reading flow the section is built around — searching is a way
 * to FIND a topic, and the TOC is the list of topics.
 *
 * The query state lives in `HelpView` so the same string drives both this input
 * and the filtered list; this component owns only presentation + the clear
 * affordance.
 */
export interface HelpSearchProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Number of topics currently matching — announced to screen readers. */
  readonly resultCount: number;
}

export function HelpSearch({ value, onChange, resultCount }: HelpSearchProps): React.JSX.Element {
  const active = value.trim().length > 0;
  return (
    <div className="relative">
      <label htmlFor="help-search-input" className="sr-only">
        Search help topics
      </label>
      <i
        className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-muted"
        aria-hidden
      />
      <input
        id="help-search-input"
        data-testid="help-search"
        type="search"
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search topics…"
        className="w-full rounded-md border border-border bg-surface py-2 pl-8 pr-8 text-sm text-text outline-none transition placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/25"
      />
      {active ? (
        <button
          type="button"
          data-testid="help-search-clear"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-text-muted transition hover:bg-surface-muted hover:text-text-strong"
        >
          <i className="fa-solid fa-xmark text-xs" aria-hidden />
        </button>
      ) : null}
      {/* Announce the result count to assistive tech; the visible empty state
          lives in the TOC list itself. */}
      <p className="sr-only" role="status" aria-live="polite">
        {active
          ? `${resultCount} ${resultCount === 1 ? 'topic' : 'topics'} match your search`
          : `${resultCount} topics`}
      </p>
    </div>
  );
}
